import { ChevronDown } from "lucide-react";
import { useId, useMemo, useState } from "react";

import { useAuth } from "../../auth/AuthContext";
import { groupEventsForPicker } from "../../lib/eventTiming";
import { trpc } from "../../trpc";
import type { EventGroup } from "../event-registrations/shared";

import { EventEmails } from "./EventEmails";
import { OneOffEmails } from "./OneOffEmails";
import { SentHistory } from "./SentHistory";
import { bookings, people } from "./sendCopy";
import { BODY_TEXT } from "./ui";

function optionText(group: EventGroup, today: boolean): string {
  const date = group.eventDateLabel ? `, ${group.eventDateLabel}` : "";
  return `${group.eventTitle}${date}${today ? " (today)" : ""}`;
}

/**
 * The Emails page: the only place the team sends an email to many people
 * at once. Choose an event, choose one of its emails, test it, send it,
 * and see what has already gone out. Organisers only (the backend's send
 * procedures are organiser-level), Australia only.
 */
export function EmailsHub() {
  const { user, role } = useAuth();
  const selectId = useId();
  const chooseHeadingId = useId();

  const eventsQuery = trpc.adminListEventRegistrations.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: true,
  });
  const historyQuery = trpc.adminListEmailSends.useQuery(undefined, {
    retry: false,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });

  const events = useMemo(
    () => eventsQuery.data?.events ?? [],
    [eventsQuery.data],
  );
  const picker = useMemo(() => groupEventsForPicker(events), [events]);

  // Until the sender picks, show the next event that hasn't finished.
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const group: EventGroup | null = useMemo(() => {
    const chosen = selectedSlug
      ? events.find((g) => g.eventSlug === selectedSlug)
      : undefined;
    return (
      chosen ??
      picker.today[0] ??
      picker.comingUp[0] ??
      picker.finished[0] ??
      picker.everyWeek[0] ??
      null
    );
  }, [events, picker, selectedSlug]);

  const defaultTestEmail = user?.email ?? "";
  const senderName = user?.fullName ?? null;
  const history = historyQuery.data;
  const todaySlugs = new Set(picker.today.map((g) => g.eventSlug));
  const optionGroups: { label: string; groups: EventGroup[] }[] = [
    { label: "Coming up", groups: [...picker.today, ...picker.comingUp] },
    { label: "Every week", groups: picker.everyWeek },
    { label: "Finished", groups: picker.finished },
  ];

  return (
    <section className="mx-auto max-w-[1100px] px-4 py-8 sm:px-7 md:px-10 md:py-12">
      <header className="border-b border-[color:var(--color-line)] pb-6">
        <h1 className="page-title">Emails</h1>
        <p className={`mt-2 max-w-2xl ${BODY_TEXT}`}>
          Send emails to guests and see what has already gone out. Every email
          comes to you first so you can check it.
        </p>
      </header>

      <section aria-labelledby={chooseHeadingId} className="mt-8">
        <h2
          id={chooseHeadingId}
          className="text-title font-bold text-[color:var(--color-ink)]"
        >
          1. Choose the event
        </h2>

        {eventsQuery.isLoading ? (
          <p className={`mt-3 ${BODY_TEXT}`}>Loading events…</p>
        ) : eventsQuery.error ? (
          <p className="mt-3 text-[16px] text-[color:var(--color-danger)]">
            Couldn't load the events. Check your internet connection and refresh
            the page.
          </p>
        ) : group ? (
          <>
            <label
              htmlFor={selectId}
              className="mt-4 block text-[15px] font-semibold text-[color:var(--color-ink)]"
            >
              Which event is this email about?
            </label>
            <div className="relative mt-2 md:max-w-xl">
              <select
                id={selectId}
                value={group.eventSlug}
                onChange={(e) => setSelectedSlug(e.target.value)}
                className="min-h-12 w-full appearance-none rounded-md border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] py-2 pr-10 pl-3.5 text-[16px] text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-amber)]"
              >
                {optionGroups.map(({ label, groups }) =>
                  groups.length === 0 ? null : (
                    <optgroup key={label} label={label}>
                      {groups.map((g) => (
                        <option key={g.eventSlug} value={g.eventSlug}>
                          {optionText(g, todaySlugs.has(g.eventSlug))}
                        </option>
                      ))}
                    </optgroup>
                  ),
                )}
              </select>
              <ChevronDown
                size={18}
                aria-hidden
                className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-[color:var(--color-ink-soft)]"
              />
            </div>
            <p className="mt-2 text-[15px] text-[color:var(--color-ink-soft)] tabular-nums">
              {group.eventDateLabel ? `${group.eventDateLabel} · ` : ""}
              {bookings(group.registrationCount)}, {people(group.totalPeople)}
            </p>
          </>
        ) : (
          <p className={`mt-3 ${BODY_TEXT}`}>
            No one has booked for an event yet, so there is nothing to send.
          </p>
        )}
      </section>

      {group ? (
        <EventEmails
          key={group.eventSlug}
          group={group}
          events={events}
          history={history}
          defaultTestEmail={defaultTestEmail}
          senderName={senderName}
        />
      ) : null}

      <OneOffEmails
        history={history}
        historyLoaded={historyQuery.isSuccess}
        isOrganiser={role === "superadmin"}
        defaultTestEmail={defaultTestEmail}
        senderName={senderName}
      />

      <SentHistory
        history={history}
        isLoading={historyQuery.isLoading}
        loadFailed={Boolean(historyQuery.error)}
        eventSlug={group?.eventSlug ?? null}
        eventTitle={group?.eventTitle ?? null}
      />
    </section>
  );
}
